import { useCallback, useEffect, useState } from "react";
import { App, Button, Card, Col, Descriptions, Empty, Form, Modal, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import { ExportOutlined, LinkOutlined } from "@ant-design/icons";
import type {
  ClientSummaryResponse,
  SearchConversion,
  UserSearchRecord,
  UserSearchesResponse,
} from "../../types/userSearches";
import { FiltersCell } from "../../components/FiltersPreview";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";
import { buildAuctionUrl, buildFrontFilterUrl } from "../../lib/frontFilterLink";
import "./list.css";

const { Title, Paragraph, Text } = Typography;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const PAGE_SIZE = 100;

// Churn-risk threshold: a client whose zero-result share crosses this is worth a call.
const ZERO_RATIO_WARN = 0.3;

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { ...adminHeaders(), ...init?.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pl-PL");
}

function daysSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / 86_400_000);
}

function rowKey(record: UserSearchRecord): string {
  return record._id ?? `${record.created_at ?? ""}-${record.query ?? ""}`;
}

// "Open the same list the client saw" — reconstructs the front-end /ogloszenia URL from
// params_jsonb. `stop` prevents the surrounding table row's click (which opens the modal).
function OpenAsUserLink({ record, stop = false }: { record: UserSearchRecord; stop?: boolean }) {
  const href = buildFrontFilterUrl(record.params_jsonb);
  return (
    <Button
      type="link"
      size="small"
      icon={<ExportOutlined />}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stop ? (e) => e.stopPropagation() : undefined}
    >
      Otwórz jak user
    </Button>
  );
}

function ConversionsCell({ conversions }: { conversions: SearchConversion[] | null | undefined }) {
  if (!conversions || conversions.length === 0) return <Text type="secondary">—</Text>;
  return (
    <Tooltip
      title={conversions
        .map((c) => `${c.auction_id ?? "?"}${c.position != null ? ` (poz. ${c.position})` : ""}${c.source ? ` · ${c.source}` : ""}`)
        .join("\n")}
    >
      <Tag color="green">{conversions.length} klik.</Tag>
    </Tooltip>
  );
}

// Row-click detail: the search context (phrase, filters, result count) plus the tracked
// conversions for that search — each tender deep-linked to the front. Reads only fields the
// list endpoint already returns, so it works on enriched and legacy docs alike.
function SearchDetailModal({
  record,
  open,
  onClose,
}: {
  record: UserSearchRecord | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!record) return null;
  const conversions = record.conversions ?? [];
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Szczegóły wyszukiwania i konwersji"
      width={760}
      footer={[
        <OpenAsUserLink key="open" record={record} />,
        <Button key="close" onClick={onClose}>
          Zamknij
        </Button>,
      ]}
    >
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Data">{formatDate(record.created_at)}</Descriptions.Item>
        <Descriptions.Item label="Fraza">
          {record.query ? record.query : <Text type="secondary">(bez frazy)</Text>}
        </Descriptions.Item>
        <Descriptions.Item label="Liczba wyników">
          {record.zero_results ? (
            <Tag color="error">0 wyników</Tag>
          ) : record.results_count !== undefined ? (
            record.results_count
          ) : (
            "—"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Filtry">
          <FiltersCell filters={record.filters_display} full />
        </Descriptions.Item>
      </Descriptions>

      <Title level={5}>Konwersje ({conversions.length})</Title>
      {conversions.length === 0 ? (
        <Empty description="Brak kliknięć w wyniki tego wyszukiwania." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Table<SearchConversion>
          size="small"
          rowKey={(c, index) => `${c.auction_id ?? "?"}-${index ?? 0}`}
          dataSource={conversions}
          pagination={false}
        >
          <Table.Column<SearchConversion>
            title="Ogłoszenie"
            dataIndex="auction_id"
            render={(auctionId: string | undefined) => {
              const url = buildAuctionUrl(auctionId);
              if (!auctionId) return <Text type="secondary">—</Text>;
              return url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <Space size={4}>
                    <LinkOutlined />
                    {auctionId}
                  </Space>
                </a>
              ) : (
                auctionId
              );
            }}
          />
          <Table.Column<SearchConversion>
            title="Pozycja"
            dataIndex="position"
            width={90}
            render={(position: number | undefined) => (position != null ? position : "—")}
          />
          <Table.Column<SearchConversion>
            title="Źródło"
            dataIndex="source"
            width={140}
            render={(source: string | undefined) => (source ? <Tag>{source}</Tag> : "—")}
          />
          <Table.Column<SearchConversion>
            title="Czas kliknięcia"
            dataIndex="clicked_at"
            render={(clickedAt: string | undefined) => formatDate(clickedAt)}
          />
        </Table>
      )}
    </Modal>
  );
}

function ClientSummaryCards({ summary }: { summary: ClientSummaryResponse }) {
  const { search, conversions } = summary;
  const zeroPct = Math.round((search.zero_ratio ?? 0) * 100);
  const atRisk = search.total >= 5 && (search.zero_ratio ?? 0) >= ZERO_RATIO_WARN;
  const inactiveDays = daysSince(search.last_search_at);

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic title="Wyszukania" value={search.total} />
          <Text type="secondary">
            ostatnie: {formatDate(search.last_search_at)}
            {inactiveDays != null && inactiveDays >= 21 ? ` (${inactiveDays} dni temu)` : ""}
          </Text>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title="Zero-wyników"
            value={zeroPct}
            suffix="%"
            valueStyle={atRisk ? { color: "#cf1322" } : undefined}
          />
          <Text type="secondary">{search.zero_results} z {search.total}</Text>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic title="Konwersje (kliki/eventy)" value={conversions.total} />
          <Text type="secondary">ostatnia: {formatDate(conversions.last_conversion_at)}</Text>
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <div style={{ marginBottom: 8, color: "rgba(0,0,0,0.45)", fontSize: 14 }}>Sygnał opiekuna</div>
          {atRisk ? (
            <Tag color="error">ryzyko churn — dużo zero-wyników</Tag>
          ) : inactiveDays != null && inactiveDays >= 21 ? (
            <Tag color="warning">nieaktywny {inactiveDays} dni</Tag>
          ) : (
            <Tag color="success">OK</Tag>
          )}
        </Card>
      </Col>
    </Row>
  );
}

function FilterAdoption({ summary }: { summary: ClientSummaryResponse }) {
  const { search, conversions } = summary;
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} md={12}>
        <Card size="small" title="Adopcja nowych filtrów (po redesignie)">
          <Space size={[8, 8]} wrap style={{ marginBottom: 12 }}>
            <Tag color={search.uses_industries ? "geekblue" : "default"}>
              Branże: {search.uses_industries ? "używa" : "nie używa"}
            </Tag>
            <Tag color={search.uses_cpv ? "purple" : "default"}>
              CPV: {search.uses_cpv ? "używa" : "nie używa"}
            </Tag>
          </Space>
          {search.industries.length > 0 ? (
            <Space size={[4, 4]} wrap>
              {search.industries.map((industry) => (
                <Tag key={industry.slug} color="geekblue">
                  {industry.name} · {industry.count}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">Klient nie filtrował po branżach.</Text>
          )}
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card size="small" title="Konwersje wg źródła / typu">
          {conversions.total === 0 ? (
            <Text type="secondary">Brak zarejestrowanych konwersji.</Text>
          ) : (
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Źródła">
                <Space size={[4, 4]} wrap>
                  {conversions.by_source.map((row) => (
                    <Tag key={row.value}>{row.value}: {row.count}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Typy">
                <Space size={[4, 4]} wrap>
                  {conversions.by_event.map((row) => (
                    <Tag key={row.value} color="blue">{row.value}: {row.count}</Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>
      </Col>
    </Row>
  );
}

export const UserSearchesList = () => {
  const { notification } = App.useApp();
  const [userId, setUserId] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  const [activeOnly, setActiveOnly] = useState(true);
  const { users, loading: usersLoading } = useSupabaseUsers(activeOnly, [userId]);

  const [searches, setSearches] = useState<UserSearchRecord[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const [summary, setSummary] = useState<ClientSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [detailRecord, setDetailRecord] = useState<UserSearchRecord | null>(null);

  const loadSearches = useCallback(
    async (selectedUserId: string, page: number) => {
      setSearchesLoading(true);
      try {
        const offset = (page - 1) * PAGE_SIZE;
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(offset),
        });
        const response = await adminFetch<UserSearchesResponse>(
          `${API_URL}/api/v1/admin/supabase/users/${encodeURIComponent(selectedUserId)}/searches?${params.toString()}`,
        );
        setSearches(response.data ?? []);
        setTotal(response.total ?? 0);
      } catch (error) {
        setSearches([]);
        setTotal(0);
        notification.error({
          message: "Nie udało się pobrać wyszukiwań",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setSearchesLoading(false);
      }
    },
    [notification],
  );

  const loadSummary = useCallback(
    async (selectedUserId: string) => {
      setSummaryLoading(true);
      try {
        const response = await adminFetch<ClientSummaryResponse>(
          `${API_URL}/api/v1/admin/clients/${encodeURIComponent(selectedUserId)}/summary`,
        );
        setSummary(response);
      } catch (error) {
        setSummary(null);
        notification.error({
          message: "Nie udało się pobrać podsumowania klienta",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setSummaryLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    if (userId) {
      loadSearches(userId, currentPage);
    } else {
      setSearches([]);
      setTotal(0);
    }
  }, [userId, currentPage, loadSearches]);

  useEffect(() => {
    if (userId) {
      loadSummary(userId);
    } else {
      setSummary(null);
    }
  }, [userId, loadSummary]);

  const handleUserChange = (value: string | undefined) => {
    setUserId(value);
    setCurrentPage(1);
  };

  return (
    <Card>
      <Title level={4}>Klient — wyszukiwania i konwersje</Title>
      <Paragraph type="secondary">
        Perspektywa per klient dla moderatorów i opiekunów: historia wyszukiwań z czytelnymi
        branżami, odsetek zero-wyników (sygnał ryzyka churn), adopcja nowych filtrów i konwersje.
      </Paragraph>

      <Form layout="vertical" style={{ maxWidth: 480, marginBottom: 24 }}>
        <Form.Item
          label="Klient"
          required
          extra={<ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />}
        >
          <Select
            showSearch
            placeholder="Wybierz klienta"
            loading={usersLoading}
            value={userId}
            onChange={handleUserChange}
            optionFilterProp="label"
            options={users.map((u) => ({ value: u.id, label: u.email || u.id }))}
            allowClear
          />
        </Form.Item>
      </Form>

      {!userId && <Empty description="Wybierz klienta, aby zobaczyć jego aktywność." />}

      {userId && summary && !summaryLoading && (
        <>
          <ClientSummaryCards summary={summary} />
          <FilterAdoption summary={summary} />
        </>
      )}

      {userId && (
        <Table<UserSearchRecord>
          rowKey={rowKey}
          dataSource={searches}
          loading={searchesLoading}
          rowClassName={(record) => `row-clickable${record.zero_results ? " row-zero-results" : ""}`}
          onRow={(record) => ({
            onClick: () => setDetailRecord(record),
          })}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: true }}
        >
          <Table.Column<UserSearchRecord>
            dataIndex="created_at"
            title="Data"
            width={180}
            render={(value: string | undefined) => formatDate(value)}
          />
          <Table.Column<UserSearchRecord>
            dataIndex="query"
            title="Zapytanie"
            render={(value: string | undefined) => value || <Text type="secondary">(bez frazy)</Text>}
          />
          <Table.Column<UserSearchRecord>
            dataIndex="results_count"
            title="Wyniki"
            width={110}
            render={(value: number | undefined, record) =>
              record.zero_results ? (
                <Tag color="error">0 wyników</Tag>
              ) : value !== undefined ? (
                value
              ) : (
                "—"
              )
            }
          />
          <Table.Column<UserSearchRecord>
            title="Branże / filtry"
            render={(_: unknown, record) => <FiltersCell filters={record.filters_display} />}
          />
          <Table.Column<UserSearchRecord>
            title="Kliknięcia"
            width={110}
            render={(_: unknown, record) => <ConversionsCell conversions={record.conversions} />}
          />
          <Table.Column<UserSearchRecord>
            title="Akcje"
            width={150}
            render={(_: unknown, record) => <OpenAsUserLink record={record} stop />}
          />
        </Table>
      )}

      <SearchDetailModal
        record={detailRecord}
        open={detailRecord !== null}
        onClose={() => setDetailRecord(null)}
      />
    </Card>
  );
};
